import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { getMyOrder } from "../../../api/website/paylater/payLater";
import Container from "../../../utils/Container";
import PayNow from "./PayNow";
// TODO: fix this path to where you saved the new PaymentMethod.jsx

const STATUS_OPTIONS = [
  { value: 0, label: "Not confirmed" },
  { value: 1, label: "Ordered" },
  { value: 2, label: "Refunded" },
  { value: 3, label: "Cancelled" },
];

const statusBadge = (status) => {
  switch (Number(status)) {
    case 0:
      return "bg-yellow-100 text-yellow-700";
    case 2:
      return "bg-blue-100 text-blue-700";
    case 3:
      return "bg-red-100 text-red-700";
    default:
      return "bg-green-100 text-green-700";
  }
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-800 font-medium text-right break-all">
      {value ?? "-"}
    </span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <h3 className="text-base font-semibold text-gray-800 mb-3">{title}</h3>
    {children}
  </div>
);

const Paylater = () => {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(1);
  const [saving] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);

  const load = () => {
    getMyOrder(
      invoiceId,
      (d) => {
        setData(d);
        setStatus(Number(d?.order?.status) || 0);
      },
      setIsLoading,
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [invoiceId]);

  if (isLoading || !data) {
    return (
      <div className="p-10 text-center text-gray-500 font-semibold">
        Loading order details...
      </div>
    );
  }

  const { order, hotel, customer, rooms = [], guests = [] } = data;

  const money = (v) => `${Number(v || 0).toFixed(0)} BDT`;
  const discountedAmount = rooms.reduce(
    (total, room) => total + Number(room.discountedPrice || 0),
    0,
  );

  const serviceCharge = rooms.reduce(
    (total, room) => total + Number(room.taxAmount || 0),
    0,
  );

  const payable = discountedAmount + serviceCharge;
  const due = payable - (Number(order.paidAmount) || 0);

  // Same formula the server uses when it charges: totalAmount - paidAmount
  const advancePayment = Number(hotel.advancePayment) || 0;

  const amountDue = Math.max(
    0,
    Math.round(
      ((Number(order.totalAmount) || 0) * (100 - advancePayment)) / 100,
    ) - (Number(order.paidAmount) || 0),
  );

  // Show "Pay Now" only for an unpaid order that is not cancelled/refunded
  const canPay =
    order.paymentStatus !== "Paid" &&
    ![2, 3].includes(Number(order.status)) &&
    amountDue > 0;

  return (
    <Container>
      <div className="space-y-5 py-20">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                Order #{order.invoiceId}
              </h2>
              <p className="text-xs text-gray-500">
                {order.createdAt?.slice(0, 10)}
              </p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge(
                status,
              )}`}
            >
              {STATUS_OPTIONS.find((o) => o.value === status)?.label}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* PAY NOW */}
            {canPay && (
              <button
                onClick={() => setOpenPayment(true)}
                className="bg-[#161f52] hover:bg-[#0a0e25] duration-300 text-white text-sm font-bold py-2 px-5 rounded-lg"
              >
                Pay Now {amountDue} BDT
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Hotel */}
          <Section title="Hotel">
            <div className="flex items-center gap-3 mb-3">
              {hotel.hotelLogo && (
                <img
                  src={hotel.hotelLogo}
                  alt=""
                  className="w-14 h-14 object-contain rounded-lg border border-gray-100"
                />
              )}
              <div>
                <p className="font-semibold text-gray-800">{hotel.hotelName}</p>
                <p className="text-xs text-gray-500">
                  {[hotel.area, hotel.city, hotel.country]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
            </div>
          </Section>

          <Section title="Customer">
            <Row label="Name" value={customer.fullName} />
            <Row label="Email" value={customer.email} />
            <Row label="Phone" value={customer.phoneNumber} />
          </Section>

          <Section title="Booking">
            <Row label="Invoice ID" value={order.invoiceId} />
            <Row label="Transaction ID" value={order.transactionId} />
            <Row label="Check-In" value={hotel.checkIn} />
            <Row label="Check-Out" value={hotel.checkOut} />
            <Row label="Total Rooms" value={`${rooms.length} rooms`} />
            <Row
              label="Total Guests"
              value={
                guests.length > 0 ? `${guests.length} persons` : "Not Mentioned"
              }
            />
          </Section>

          {/* Payment */}
          <Section title="Payment">
            <Row label="Total Amount" value={money(order.totalAmount)} />
            <Row label="Discounted Amount" value={money(discountedAmount)} />
            <Row label="Service Charge" value={money(serviceCharge)} />
            <Row label="Paid Amount" value={money(order.paidAmount)} />
            <Row label="Due" value={money(due > 0 ? due : 0)} />
            <Row label="Payment Status" value={order.paymentStatus} />
            <Row label="Payment Nature" value={order.paymentNature} />
            <Row
              label="Payment Method"
              value={order.paymentSource || order.paymentMethod}
            />
          </Section>
        </div>

        {/* Guests */}
        {guests.length > 0 && (
          <Section title="Guests">
            <div className="flex flex-wrap gap-2">
              {guests.map((g, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                >
                  {g}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Rooms + per-night breakdown */}
        <Section title="Rooms">
          <div className="space-y-4">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex flex-wrap justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-gray-800">
                      {room.roomName || `Room #${room.roomId}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {room.roomType} · {room.nights || 1} night(s)
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-gray-800 font-semibold">
                      {money(room.discountedPrice || room.actualPrice)}
                    </p>
                    {room.taxAmount ? (
                      <p className="text-xs text-gray-500">
                        + {money(room.taxAmount)} tax
                      </p>
                    ) : null}
                  </div>
                </div>

                {Array.isArray(room.nightly) && room.nightly.length > 0 && (
                  <div className="overflow-x-auto mt-2">
                    <table className="min-w-full text-xs border-t border-gray-100">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left py-1 pr-4">Date</th>
                          <th className="text-right py-1 pr-4">Price</th>
                          <th className="text-right py-1 pr-4">Discount</th>
                          <th className="text-right py-1">After Discount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {room.nightly.map((n) => (
                          <tr key={n.id} className="text-gray-700">
                            <td className="py-1 pr-4 text-nowrap">
                              {n.date?.slice(0, 10)}
                            </td>
                            <td className="py-1 pr-4 text-right text-nowrap">
                              {money(n.actualPrice)}
                            </td>
                            <td className="py-1 pr-4 text-right text-nowrap">
                              {Number(n.discount || 0)}%
                            </td>
                            <td className="py-1 text-right text-nowrap">
                              {money(n.discountedPrice)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* PAYMENT POPUP */}
      <PayNow
        open={openPayment}
        setOpen={setOpenPayment}
        invoiceId={order.invoiceId}
        amountDue={amountDue}
        onPaid={load}
      />
    </Container>
  );
};

export default Paylater;
